import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useLocation } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { staffSetPassword } from "@/lib/staff-api";
import { ErrorMessage } from "@/components/ErrorMessage";
import { StaffAuthCard, inputClass, linkClass, primaryButtonClass } from "./StaffAuthCard";

const setPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

const requestNewLink = (
  <p className="mt-4 text-center text-sm">
    <Link to="/staff/forgot-password" className={linkClass}>
      Request a new link
    </Link>
  </p>
);

/** Where the staff invite and reset emails land: /staff/set-password?token=… */
export function StaffSetPasswordPage(): React.ReactElement {
  const token = new URLSearchParams(useLocation().search).get("token");
  const setMutation = useMutation({
    mutationFn: ({ linkToken, password }: { linkToken: string; password: string }) =>
      staffSetPassword(linkToken, password),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordFormData>({ resolver: zodResolver(setPasswordSchema) });

  if (!token) {
    return (
      <StaffAuthCard title="Invalid link">
        <p className="text-center text-sm text-gray-600">This link is invalid or incomplete. Request a new one.</p>
        {requestNewLink}
      </StaffAuthCard>
    );
  }

  if (setMutation.isSuccess) {
    return (
      <StaffAuthCard title="Set your password">
        <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          Your password is set. You can now sign in.
        </div>
        <p className="mt-4 text-center text-sm">
          <Link to="/staff/login" className={linkClass}>
            Go to sign in
          </Link>
        </p>
      </StaffAuthCard>
    );
  }

  const onSubmit = (data: SetPasswordFormData): void => {
    setMutation.mutate({ linkToken: token, password: data.password });
  };
  const linkExpired = setMutation.error instanceof ApiError && setMutation.error.code === "INVALID_TOKEN";

  return (
    <StaffAuthCard title="Set your password">
      <ErrorMessage message={setMutation.error instanceof Error ? setMutation.error.message : undefined} />

      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="mt-4 space-y-4">
        <div>
          <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
            New password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            aria-describedby="password-hint"
            {...register("password")}
            className={inputClass}
          />
          <p id="password-hint" className="mt-1 text-xs text-gray-500">
            Must be at least 8 characters.
          </p>
          {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-gray-700">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            {...register("confirmPassword")}
            className={inputClass}
          />
          {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>}
        </div>

        <button type="submit" disabled={setMutation.isPending} className={primaryButtonClass}>
          {setMutation.isPending ? "Setting password..." : "Set password"}
        </button>
      </form>
      {linkExpired && requestNewLink}
    </StaffAuthCard>
  );
}
